import React, { Fragment, useState } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select, Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import Labels from "../labels/Labels";
import AssignedCardDetail from "../components/AssignedCardDetail";
import RenderOnRole from "./RenderOnRole";
import { useMutation, useQuery } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import DownloadIcon from "@mui/icons-material/Download";
import RefreshingIndicator from "./RefreshingIndicator";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import dayjs from "dayjs";
import { CreditCardOutlined } from "@mui/icons-material";
import SecureInfoCardDetail from "./SecureInfoCardDetail";
import AddCardIcon from "@mui/icons-material/AddCard";
import UpgradeCardDialog from "./UpgradeCardDialog";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import CardGiftcardIcon from "@mui/icons-material/CardGiftcard";
import NotListedLocationIcon from "@mui/icons-material/NotListedLocation";
import PasswordIcon from "@mui/icons-material/Password";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

function getStyles(name, personName, theme) {
  return {
    fontWeight:
      personName.indexOf(name) === -1
        ? theme.typography.fontWeightRegular
        : theme.typography.fontWeightMedium,
  };
}

export default function AssignedCards(props) {
  const [dateFromValue, setDateFromValue] = React.useState(null);
  const [dateToValue, setDateToValue] = React.useState(null);
  const [locationIds, setLocationIds] = React.useState({});
  const [productType, setProductType] = React.useState("");

  const [cleared, setCleared] = React.useState(false);

  const [elements, setElements] = React.useState([]);

  const theme = useTheme();

  // Helper: format numbers as USD currency
  const formatCurrency = (v) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 2,
    }).format(Number(v));

  // Fetch card product attributes
  async function fetchEmployerAttribute(attributeName) {
    const response = await HttpService.getAxiosClient().get(
      window.API_URL + `/employer/attributes/${attributeName}`
    );
    return response;
  }

  const { data: usePaycardAttr } = useQuery(["usePaycardAttr"], () =>
    fetchEmployerAttribute("USE_PAYCARD")
  );

  const { data: useCorporateExpenseAttr } = useQuery(
    ["useCorporateExpenseAttr"],
    () => fetchEmployerAttribute("USE_CORPORATE_EXPENSE")
  );

  const { data: useIncentiveAttr } = useQuery(["useIncentiveAttr"], () =>
    fetchEmployerAttribute("USE_INCENTIVE")
  );

  const { data: useVirtualCardAttr } = useQuery(["useVirtualCardAttr"], () =>
    fetchEmployerAttribute("USE_VIRTUAL")
  );

  const { data: useAnonymousAttr } = useQuery(["useAnonymousAttr"], () =>
    fetchEmployerAttribute("USE_REWARDS_ANONYM")
  );

  // Get available product types based on employer attributes
  const getAvailableProductTypes = () => {
    const productTypes = [];

    // Default product types
    const defaultProductTypes = [
      {
        id: "PAYCARD",
        name: Labels.getLabel("assignedCards.ProductPaycard", "Paycard"),
        icon: <CreditCardIcon />,
        description: Labels.getLabel(
          "assignedCards.PaycardDescription",
          "Standard paycard for payroll distribution"
        ),
      },
      {
        id: "CORPORATE_EXPENSE",
        name: Labels.getLabel(
          "assignedCards.ProductCorporateExpense",
          "Corporate Expense"
        ),
        icon: <AccountBalanceWalletIcon />,
        description: Labels.getLabel(
          "assignedCards.CorporateExpenseDescription",
          "Card for managing corporate expenses"
        ),
      },
      {
        id: "INCENTIVE",
        name: Labels.getLabel("assignedCards.ProductIncentive", "Incentive"),
        icon: <CardGiftcardIcon />,
        description: Labels.getLabel(
          "assignedCards.IncentiveDescription",
          "Rewards and incentives program cards"
        ),
      },
      {
        id: "REWARDS_ANONYM",
        name: Labels.getLabel("assignedCards.ProductAnonymous", "Anonymous"),
        icon: <NotListedLocationIcon />,
        description: Labels.getLabel(
          "assignedCards.AnonymousDescription",
          "Anonymous reward cards"
        ),
      },
    ];

    // Add "All Products" option
    productTypes.push({
      id: "",
      name: Labels.getLabel("assignedCards.AllProducts", "All Products"),
      icon: null,
      description: Labels.getLabel(
        "assignedCards.AllProductsDescription",
        "Show cards of all product types"
      ),
    });

    // Get attribute values from individual queries
    const usePaycard = usePaycardAttr?.data?.value === "true";
    const useCorporateExpense = useCorporateExpenseAttr?.data?.value === "true";
    const useIncentive = useIncentiveAttr?.data?.value === "true";
    const useAnonymous = useAnonymousAttr?.data?.value === "true";

    // Check if any product attributes are defined
    const hasProductAttributes =
      (usePaycard !== undefined && usePaycard === true) ||
      (useCorporateExpense !== undefined && useCorporateExpense === true) ||
      (useIncentive !== undefined && useIncentive === true) ||
      (useAnonymous !== undefined && useAnonymous === true);

    if (!hasProductAttributes) {
      // If no attributes are defined, return all product types
      return [...productTypes, ...defaultProductTypes];
    }

    // Add products based on attribute values
    if (usePaycard) {
      productTypes.push(defaultProductTypes.find((p) => p.id === "PAYCARD"));
    }

    if (useCorporateExpense) {
      productTypes.push(
        defaultProductTypes.find((p) => p.id === "CORPORATE_EXPENSE")
      );
    }

    if (useIncentive) {
      productTypes.push(defaultProductTypes.find((p) => p.id === "INCENTIVE"));
    }

    if (useAnonymous) {
      productTypes.push(
        defaultProductTypes.find((p) => p.id === "REWARDS_ANONYM")
      );
    }

    return productTypes;
  };

  const handleProductTypeChange = (event) => {
    const newProductType = event.target.value;
    setProductType(newProductType);
    props.setProductTypeFunction(newProductType);
    props.refetchFunction();
  };

  React.useEffect(() => {
    if (cleared) {
      const timeout = setTimeout(() => {
        setCleared(false);
      }, 1500);

      return () => clearTimeout(timeout);
    }
    return () => {};
  }, [cleared]);

  const {
    isLoading,
    isFetching,
    isError,
    data,
    refetchFunction,
    refetchStatsFunction,
    setPageFunction,
    setFilterStringFunction,
    setDateFromFunction,
    setDateToFunction,
    dataCount,
    isLoadingDataCount,
    pageSize,
    onPageSizeChange,
    setLocationIdsFunction,
    dataLocations,
    isLoadingLocations,
    dataIsAnonymousCard,
    isLoadingDataIsAnonymousCard,
    isErrorDataIsAnonymousCard,
    dataShowSecureCardInfo,
    isLoadingDataShowSecureCardInfo,
    isErrorDataShowSecureCardInfo,
  } = props;

  const [openNewDlg, setOpenNewDlg] = useState(false);
  const [openSecretInfoNewDlg, setOpenSecretInfoNewDlg] = useState(false);
  const [openUpgradeDialog, setOpenUpgradeDialog] = useState(false);
  const [cardIdentifier, setCardIdentifier] = useState();
  const [memberId, setMemberId] = useState();
  const [companyId, setCompanyId] = useState();
  const [cardToUpgrade, setCardToUpgrade] = useState(null);

  const [filter, setFilter] = useState("");

  const onViewButtonClick = (e, params) => {
    setCardIdentifier(params.id);
    setOpenNewDlg(true);
  };

  const onViewSecretInfoButtonClick = (e, params) => {
    setCardIdentifier(params.id);
    setMemberId(params.employeeNo);
    setCompanyId(props.employerIdentifier);
    setOpenSecretInfoNewDlg(true);
  };

  const onFilterChange = (filter, params) => {
    const filterString = filter.quickFilterValues.join(" ");
    if (filterString === undefined) {
      setFilterStringFunction("");
      setFilter("");
    } else {
      setFilterStringFunction(filterString);
      setFilter(filterString);
    }
    return filter;
  };

  const onAssignButtonClick = (e, params) => {
    setCardToUpgrade(params);
    setOpenUpgradeDialog(true);
  };

  const dataColumns = [
    {
      field: "id",
      headerName: Labels.getLabel("assignedCards.column.id", "ID"),
      width: 70,
      sortable: false,
    },
    {
      field: "cardNumber",
      headerName: Labels.getLabel("assignedCards.column.last4", "Last 4"),
      width: 140,
      sortable: false,
      headerAlign: "center",
      align: "center",

      renderCell: (params) => {
        return (
          <div
            style={{
              minWidth: "4rem",
              display: "flex",
              flexDirection: "column",
              alignContent: "center",
              alignItems: "center",
            }}
          >
            <Typography
              sx={{ fontSize: 12, mb: 0, mt: 0.5, fontWeight: "bold" }}
              gutterBottom
            >
              {params.row.cardNumber}
            </Typography>
            <Typography
              sx={{
                fontSize: 6,
                mb: 0,
                mt: 0.5,
                fontWeight: "normal",
                color: "grey",
              }}
              gutterBottom
            >
              {params.row.cardProxy}
            </Typography>
          </div>
        );
      },
    },

    {
      field: "accountInfo",
      headerName: Labels.getLabel(
        "assignedCards.column.accountInfo",
        "Account Info"
      ),
      hidden: dataIsAnonymousCard?.data?.value === "true",
      width: 200,
      renderCell: (params) => {
        return (
          <div
            style={{
              minWidth: "4rem",
              display: params.row.cardType === "PAYCARD" ? "flex" : "none",
              flexDirection: "column",
              alignContent: "center",
              alignItems: "center",
            }}
          >
            {/* Omitted by the backend until KYC passes when the employer sets
                HIDE_ACCOUNT_NUMBERS_UNTIL_KYC; show each line only when present. */}
            {params.row.accountNumber && (
              <Typography sx={{ fontSize: 12, mb: 0, mt: 0.5 }} gutterBottom>
                {`ACC#:${params.row.accountNumber}`}
              </Typography>
            )}
            {params.row.routingNumber && (
              <Typography
                sx={{
                  fontSize: 6,
                  mb: 0,
                  mt: 0.5,
                  fontWeight: "bold",
                  color: "grey",
                }}
                gutterBottom
              >
                {`ROU#:${params.row.routingNumber}`}
              </Typography>
            )}
          </div>
        );
      },
    },

    {
      field: "employeeNo",
      headerName: Labels.getLabel(
        "assignedCards.column.memberNo",
        "Member No."
      ),
      width: 190,
      sortable: false,
      renderCell: (params) => {
        return (
          <Typography fontSize={14} sx={{ width: "33%", flexShrink: 0 }}>
            {params.row.employeeNo}
          </Typography>
        );
      },
    },
    {
      field: "extraInfo01",
      headerName: Labels.getLabel(
        "assignedCards.column.memberInfo",
        "Member Info"
      ),
      width: 280,
      sortable: false,
      hidden: dataIsAnonymousCard?.data?.value === "true",
      renderCell: (params) => {
        const amount = params?.row?.amount;
        return (
          <Box
            sx={{
              minWidth: "4rem",
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              textAlign: "left",
              gap: 0.25,
            }}
          >
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.5, mt: 0.5 }}
            >
              <Typography
                sx={{ fontSize: 12, fontWeight: "bold", paddingRight: "5px" }}
              >
                {params.row.extraInfo01}
              </Typography>
              {amount != null && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={formatCurrency(amount)}
                  sx={{
                    //borderColor: (theme) => theme.palette.primary,
                    minWidth: "100px",
                    //color: (theme) => theme.palette.success.main,
                    "& .MuiChip-icon": { color: "inherit" },
                  }}
                />
              )}
            </Box>
            <Typography
              sx={{ fontSize: 6, fontWeight: "normal", color: "grey" }}
            >
              {params.row.email?.startsWith("paycard_noemail_")
                ? "No Email"
                : params.row.email}{" "}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "locationDescription",
      headerName: Labels.getLabel("assignedCards.column.location", "location"),
      width: 160,
      sortable: false,
      renderCell: (params) => {
        const locationDescription = params.row.locationDescription;
        const departmentName = params.row.departmentName;

        const tooltipTitle = (
            <Box>
              <div>{locationDescription}</div>
              {departmentName && (
                  <div><strong>{ Labels.getLabel("assignedCards.requestedBy", "Requested by:") }</strong> {departmentName}</div>
              )}
            </Box>
        );

        return (
            <Tooltip title={tooltipTitle} arrow>
              <Box sx={{ width: "33%", flexShrink: 0 }}>
                <Typography fontSize={14}>{locationDescription}</Typography>
                {departmentName && (
                    <Typography fontSize={12} sx={{ color: "gray" }}>
                      <strong> { Labels.getLabel("assignedCards.requestedBy", "Requested by:") } </strong>  {departmentName}
                    </Typography>
                )}
              </Box>
            </Tooltip>

        );
      },
    },
    {
      field: "cardType",
      headerName: Labels.getLabel(
        "assignedCards.column.productType",
        "Product Type"
      ),
      width: 145,
      sortable: false,
      renderCell: (params) => {
        const getIconForCardType = (cardType) => {
          switch (cardType) {
            case "PAYCARD":
              return <CreditCardIcon color="primary" />;
            case "CORPORATE_EXPENSE":
              return <AccountBalanceWalletIcon color="primary" />;
            case "INCENTIVE":
              return <CardGiftcardIcon color="primary" />;
            case "REWARDS_ANONYM":
              return <NotListedLocationIcon color="primary" />;
            default:
              return <CreditCardIcon color="primary" />;
          }
        };

        const productNames = {
          PAYCARD: Labels.getLabel("availableCards.productPaycard", "Paycard"),
          CORPORATE_EXPENSE: Labels.getLabel(
            "availableCards.productCorporate",
            "Corporate"
          ),
          INCENTIVE: Labels.getLabel(
            "availableCards.productIncentive",
            "Incentive"
          ),
          VIRTUAL: Labels.getLabel("availableCards.productVirtual", "Virtual"),
          REWARDS_ANONYM: Labels.getLabel(
            "availableCards.productAnonymous",
            "Anonymous"
          ),
        };

        return (
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <Box sx={{ mr: 1 }}>{getIconForCardType(params.row.cardType)}</Box>
            <Typography fontSize={14}>
              {productNames[params.row.cardType]}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "assignDate",
      headerName: Labels.getLabel(
        "assignedCards.column.assignedOn",
        "Assigned On"
      ),
      width: 240,
      sortable: false,
      renderCell: (params) => {
        const assignDate =
          params.row.assignDate &&
          dayjs(params.row.assignDate).format("MM/DD/YYYY");
        const assignedBy = params.row.assignedBy;
        const organizationalUnit =
          params.row.assignedFromEmployerName ||
          params.row.assignedFromEmployerId;

        const assignedByLabel = Labels.getLabel(
          "assignedCards.assignedBy",
          "Assigned by:"
        );
        const organizationalUnitLabel = Labels.getLabel(
          "assignedCards.assignedFrom",
          "Assigned from:"
        );

        const tooltipTitle = (
          <Box>
            <div>{assignDate}</div>
            {assignedBy && (
              <div>
                <strong>{assignedByLabel}</strong> {assignedBy}
              </div>
            )}
            {organizationalUnit && (
              <div>
                <strong>{organizationalUnitLabel}</strong> {organizationalUnit}
              </div>
            )}
          </Box>
        );

        return (
          <Tooltip title={tooltipTitle} arrow>
            <Box sx={{ flexShrink: 0 }}>
              <Typography fontSize={14}>{assignDate}</Typography>
              {assignedBy && (
                <Typography fontSize={12} sx={{ color: "gray" }}>
                  <strong>{assignedByLabel}</strong> {assignedBy}
                </Typography>
              )}
              {organizationalUnit && (
                <Typography fontSize={12} sx={{ color: "gray" }}>
                  <strong>{organizationalUnitLabel}</strong>{" "}
                  {organizationalUnit}
                </Typography>
              )}
            </Box>
          </Tooltip>
        );
      },
    },
    {
      field: "details",
      headerName: Labels.getLabel("assignedCards.column.details", "Details"),
      width: 80,
      sortable: false,
      hidden: dataIsAnonymousCard?.data?.value === "true",
      renderCell: (params) => {
        if (
          params.row.cardType === "INCENTIVE" ||
          params.row.cardType === "REWARDS_ANONYM"
        )
          return "";
        return (
          <RenderOnRole roles={["pc-view-detail"]}>
            <Button style={{ minWidth: "10px" }}>
              <VisibilityIcon
                onClick={(e) => onViewButtonClick(e, params.row)}
                variant="contained"
              />
            </Button>
          </RenderOnRole>
        );
      },
    },
    {
      field: "cardInfo",
      headerName: Labels.getLabel("assignedCards.column.actions", "Actions"),
      width: 160,
      sortable: false,
      renderCell: (params) => {
        return (
          <div>
            <RenderOnRole roles={["pc-read-card-secret-info"]}>
              {" "}
              {/* TODO create a new role for this option */}
              <Button style={{ minWidth: "10px" }}>
                <PasswordIcon
                  onClick={(e) => onViewSecretInfoButtonClick(e, params.row)}
                  variant="contained"
                />
              </Button>
            </RenderOnRole>
            {props.dataPaycardAllowRecarding?.data?.value === "true" &&
              params?.row?.cardType !== "INCENTIVE" &&
              params?.row?.recardPossible === true && (
                <RenderOnRole roles={["pc-assign-employee"]}>
                  <Button
                    onClick={(e) => onAssignButtonClick(e, params.row)}
                    variant="contained"
                    sx={{
                      textTransform: "none",
                      fontWeight: "bold",
                    }}
                  >
                    <AddCardIcon
                      sx={{
                        fontSize: "2rem",
                        paddingRight: "0.5rem",
                      }}
                    />
                    {Labels.getLabel("assignedCards.Upgrade", "Upgrade")}
                  </Button>
                </RenderOnRole>
              )}
          </div>
        );
      },
    },
  ];

  const downloadAssignedMutation = useMutation(
    (params) => {
      const locations =
        Array.from(locationIds).length > 0
          ? Array.from(locationIds).join(",")
          : null;

      return HttpService.getAxiosClient().get(
        window.API_URL +
          `/paycards/assigned/export?filter=` +
          filter +
          `&from=` +
          dateFromValue +
          `&to=` +
          dateToValue +
          (locations !== null ? `&locations=` + locations : "") +
          (productType ? `&productType=` + productType : ""),
        { responseType: "blob" }
      );
    },
    {
      onSuccess: (response) => {
        const { headers, data } = response;
        const disposition = headers["content-disposition"];
        let filename = disposition.split(/;(.+)/)[1].split(/=(.+)/)[1];
        if (filename.toLowerCase().startsWith("utf-8''"))
          filename = decodeURIComponent(filename.replace("utf-8''", ""));
        else filename = filename.replace(/['"]/g, "");
        let url = window.URL.createObjectURL(data);
        let a = document.createElement("a");
        a.href = url;
        a.download = filename;
        document.body.appendChild(a); // append the element to the dom
        a.click();
        a.remove(); // afterwards, remove the element
        setFeedback({
          open: true,
          type: "success",
          message: "The file was download succesfully",
          onClose: () => {
            setFeedback({ open: false });
          },
        });
      },
      onError: (err) => {
        if (err?.response?.status === 500) {
          setFeedback({
            open: true,
            type: "error",
            message: "There was an error trying to download the file",
          });
        } else {
          const msg = err?.response?.data ? err.response.data : err.message;
          setFeedback({
            open: true,
            type: "error",
            message: msg,
          });
        }
      },
    }
  );

  // We'll export the feedback functions to avoid unused variable warnings
  // but keep them available for future use
  const [, setFeedback] = useState({
    open: false,
    type: "error",
    message: "",
  });

  // eslint-disable-next-line no-unused-vars
  const handleFeedbackClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setFeedback({ open: false, type: "error", message: "" });
  };

  const handleChangeElements = (event) => {
    const {
      target: { value },
    } = event;
    setLocationIdsFunction(value);
    setLocationIds(value);
    //refetchFunction();
    setElements(
      // On autofill we get a stringified value.
      typeof value === "string" ? value.split(",") : value
    );
  };

  const onDownloadDetailsButtonClick = (e, params) => {
    downloadAssignedMutation.mutate(null);
  };

  return (
    <Fragment>
      <div style={{ display: "flex" }}>
        <div style={{ marginRight: "20px" }}>
          <h1>{Labels.getLabel("assignedCards.Title", "Cards assigned")}</h1>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between" }}>
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
            }}
          >
            <Button
              style={{ margin: "10px", height: "40px" }}
              onClick={onDownloadDetailsButtonClick}
              variant="contained"
              disabled={downloadAssignedMutation.isLoading}
              color={
                downloadAssignedMutation.isLoading ? "secondary" : "primary"
              }
            >
              <DownloadIcon style={{ paddingRight: "5px" }} />
              {downloadAssignedMutation.isLoading
                ? Labels.getLabel("assignedCards.Downloading", "Downloading...")
                : Labels.getLabel(
                    "assignedCards.ExportButton",
                    "Export Assigned Cards"
                  )}
            </Button>
          </div>
        </div>
        {isFetching ? <RefreshingIndicator /> : ""}
      </div>
      <div
        style={{
          textAlign: "right",
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <FormControl
          sx={{ m: 1, width: 500, display: "flex", justifyContent: "flex-end" }}
        >
          <InputLabel id="demo-multiple-chip-label">
            {Labels.getLabel("assignedCards.Locations", "Locations")}
          </InputLabel>
          <Select
            //disabled={disableGroupOptions}
            labelId="demo-multiple-chip-label"
            id="demo-multiple-chip"
            multiple
            value={elements}
            onChange={handleChangeElements}
            input={
              <OutlinedInput id="select-multiple-chip" label="LocationsChip" />
            }
            renderValue={(selected, other) => (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                {selected.map((key) => {
                  return (
                    <Chip
                      key={key}
                      label={
                        dataLocations?.data.find((item) => item.id === key)
                          .description
                      }
                    />
                  );
                })}
              </Box>
            )}
            MenuProps={MenuProps}
          >
            {dataLocations?.data.map((option) => (
              <MenuItem
                key={option.id}
                value={option.id}
                style={getStyles(option.description, elements, theme)}
              >
                {option.description}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Product Type Filter */}
        <FormControl sx={{ m: 1, minWidth: 250 }}>
          <InputLabel id="product-type-select-label">
            {Labels.getLabel("assignedCards.ProductType", "Product Type")}
          </InputLabel>
          <Select
            labelId="product-type-select-label"
            id="product-type-select"
            value={productType}
            label="Product Type"
            onChange={handleProductTypeChange}
          >
            {getAvailableProductTypes().map((option) => (
              <MenuItem key={option.id} value={option.id}>
                {option.icon && (
                  <Box
                    sx={{
                      mr: 1,
                      display: "inline-flex",
                      verticalAlign: "middle",
                    }}
                  >
                    {option.icon &&
                      React.cloneElement(option.icon, { color: "primary" })}
                  </Box>
                )}
                {option.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <div
          style={{
            textAlign: "right",
            display: "flex",
            justifyContent: "flex-end",
            alignContent: "center",
            alignItems: "center",
          }}
        >
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              label={Labels.getLabel("assignedCards.DateFrom", "Date from:")}
              defaultValue={null}
              value={dateFromValue}
              onChange={(newValue) => {
                setDateFromValue(newValue);
                setDateFromFunction(newValue);
                refetchFunction();
              }}
              slotProps={{
                field: { clearable: true, onClear: () => setCleared(true) },
              }}
            />
            <DatePicker
              sx={{ marginLeft: "0.5rem" }}
              label={Labels.getLabel("assignedCards.DateTo", "Date to")}
              value={dateToValue}
              onChange={(newValue) => {
                setDateToValue(newValue);
                setDateToFunction(newValue);
                refetchFunction();
              }}
              slotProps={{
                field: { clearable: true, onClear: () => setCleared(true) },
              }}
              minDate={dayjs(dateFromValue).add(1, "day")}
              disabled={dateFromValue === null}
            />
          </LocalizationProvider>
        </div>
      </div>

      {isLoading ||
      isLoadingDataCount ||
      isLoadingLocations ||
      isLoadingDataShowSecureCardInfo ||
      isLoadingDataIsAnonymousCard ? (
        <CircularProgress color="inherit" />
      ) : !isError ? (
        <div style={{ height: 500, width: "100%", paddingTop: "1rem" }}>
          <DataGrid
            disableSelectionOnClick
            disableColumnFilter
            disableColumnSelector
            disableDensitySelector
            pageSize={pageSize}
            rowsPerPageOptions={[10, 25, 50, 100]}
            onPageSizeChange={onPageSizeChange}
            columnVisibilityModel={{ id: false }}
            // The "Assigned On" cell stacks up to three lines (date, assigned by,
            // organizational unit), which does not fit the default row height.
            getRowHeight={(params) =>
              params.model?.assignedBy || params.model?.assignedFromEmployerId
                ? 76
                : null
            }
            components={{ Toolbar: GridToolbar }}
            componentsProps={{
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            rows={data?.data}
            columns={[...dataColumns.filter((item) => item.hidden !== true)]}
            rowCount={dataCount?.data}
            onPageChange={(newPage) => {
              setPageFunction(newPage);
            }}
            paginationMode="server"
            filterMode="server"
            onFilterModelChange={onFilterChange}
            sx={{
              height: "calc(55vh)",
              "& .MuiButton-sizeSmall ": { display: "none" },
              "& .MuiTypography-root": {
                fontFamily: "sans-serif",
                fontSize: "1rem",
              },
            }}
          />
        </div>
      ) : (
        <h4>
          {Labels.getLabel(
            "assignedCards.Error",
            "An error ocurred, please try again later..."
          )}
        </h4>
      )}
      {openNewDlg ? (
        <AssignedCardDetail
          action="SAVE"
          open={openNewDlg}
          setOpen={setOpenNewDlg}
          dialogTitle={Labels.getLabel(
            "assignedCards.AssignedCardDetailTitle",
            "Assigned Card Detail"
          )}
          dialogContentText="To edit the information, click the pencil."
          idBundleDetail={cardIdentifier}
        />
      ) : (
        ""
      )}
      {openSecretInfoNewDlg ? (
        <SecureInfoCardDetail
          action="SAVE"
          open={openSecretInfoNewDlg}
          setOpen={setOpenSecretInfoNewDlg}
          dialogTitle={Labels.getLabel(
            "assignedCards.SecureInfoCardDetailTitle",
            "Secure Info Card Detail"
          )}
          idBundleDetail={cardIdentifier}
          memberId={memberId}
          companyId={companyId}
        />
      ) : (
        ""
      )}
      {openUpgradeDialog ? (
        <UpgradeCardDialog
          open={openUpgradeDialog}
          onClose={(success) => {
            setOpenUpgradeDialog(false);
            setCardToUpgrade(null);
            if (success) {
              // Optionally refresh the data after successful upgrade
              refetchFunction();
            }
          }}
          cardToUpgrade={cardToUpgrade}
          employerIdentifier={props.employerIdentifier}
        />
      ) : (
        ""
      )}
    </Fragment>
  );
}
